import { type RefObject, useEffect, useState } from 'react';
import type { BaseEntity } from 'core/base';
import { useClickAway } from 'react-use';
import type { FinderProps, FinderTree } from '../types';

type UseSelectedKeysArg<TEntity extends BaseEntity> = {
  onChangeActiveKey: Exclude<FinderProps<TEntity>['onChangeActiveKey'], undefined>;
  activeKey: FinderProps<TEntity>['activeKey'];
  multiple: FinderProps<TEntity>['multiple'];
  onSelect: FinderProps<TEntity>['onSelect'];
  allowSelect: Exclude<FinderProps<TEntity>['allowSelect'], undefined>;
  tree?: FinderTree<TEntity>;
  holderRef: RefObject<HTMLDivElement | null>;
};

export const useSelectedKeys = <TEntity extends BaseEntity>(
  arg: UseSelectedKeysArg<TEntity>,
): [string[], FinderProps<TEntity>['onSelect']] => {
  const { activeKey, onChangeActiveKey, multiple, onSelect, allowSelect, tree, holderRef } = arg;
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  useEffect(() => {
    if (activeKey) {
      setSelectedKeys([activeKey]);
    }
  }, [activeKey]);

  // Reset selected keys on click outside
  useClickAway(holderRef, () => {
    if (selectedKeys.length > 1 && activeKey) {
      setSelectedKeys([activeKey]);
    }
  });

  const _onSelect: FinderProps<TEntity>['onSelect'] = (keys, info) => {
    if (onSelect) onSelect(keys, info);

    const { nativeEvent, node, selectedNodes } = info;

    if (multiple && keys.length > 1) {
      const [firstNode] = selectedNodes;

      if (nativeEvent.metaKey || nativeEvent.ctrlKey) {
        const allowedKeys = selectedNodes.filter(n => allowSelect(n, firstNode)).map(n => n.key);

        setSelectedKeys(allowedKeys);
        return;
      }

      if (nativeEvent.shiftKey) {
        const allowedKeys: string[] = [];
        const { parentNode } = firstNode;

        // Check that target node and selected nodes are allowed to be in one group
        if (!parentNode || !allowSelect(node, firstNode)) return;
        const parent = tree?.state.keyEntities[parentNode.key];

        let shouldAdd = false;

        // set initial keys for range: target node and active node
        const keysSet = new Set<string | number | undefined>([node.key, activeKey]);

        // Iterate over all children and select all gap nodes
        parent?.children?.forEach(n => {
          if (keysSet.has(n.key) || shouldAdd) {
            keysSet.delete(n.key);

            if (allowSelect(n.node, firstNode)) {
              // add all keys between target node and active node
              allowedKeys.push(n.key.toString());
            }

            shouldAdd = true;
          }

          if (keysSet.size === 0) {
            shouldAdd = false;
          }
        });

        setSelectedKeys(allowedKeys);
        return;
      }
    }

    const { key: _activeKey } = node;
    if (_activeKey) {
      onChangeActiveKey(_activeKey, info);
      setSelectedKeys([_activeKey]);
    }
  };

  return [selectedKeys, _onSelect];
};
